import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Modal } from "../../components/Modal/Modal";
import { ticketingActions } from "../../redux/actions/ticketingActions";
import {
  getTicketingIsNotificationEmailSaving,
  getTicketingNotificationEmail,
} from "../../redux/selectors/ticketingSelectors";
import * as styles from "./NotificationEmailPrompt.module.css";
import { useTicketingStrings } from "./ticketingStrings";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type NotificationEmailPromptProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
}>;

export const NotificationEmailPrompt = ({ isOpen, onClose }: NotificationEmailPromptProps) => {
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const currentEmail = useSelector(getTicketingNotificationEmail);
  const isSaving = useSelector(getTicketingIsNotificationEmailSaving);

  const [newEmail, setNewEmail] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleKeepCurrent = useCallback(() => {
    dispatch(ticketingActions.dismissNewContact());
    onClose();
  }, [onClose, dispatch]);

  const handleSaveNew = useCallback(() => {
    const trimmed = newEmail.trim();
    if (!trimmed || !emailRegex.test(trimmed)) {
      setValidationError(strings.ticketInvalidEmailMsg);
      return;
    }
    setValidationError(undefined);
    dispatch(ticketingActions.updateNotificationEmail.start(trimmed));
    setShowConfirmation(true);
  }, [newEmail, dispatch, strings.ticketInvalidEmailMsg]);

  const handleConfirmationClose = useCallback(() => {
    setShowConfirmation(false);
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSaveNew();
      }
    },
    [handleSaveNew]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={500} ariaLabelledBy="notification-email-prompt">
      <div className={styles.promptContainer}>
        {showConfirmation ? (
          <>
            <h2 id="notification-email-prompt" className={styles.promptTitle}>
              {strings.ticketPromptVerificationTitle}
            </h2>
            <p className={styles.promptDescription}>
              {strings.ticketPromptVerificationMsg.replace("{0}", newEmail.trim())}
            </p>
            <div className={styles.buttonRow}>
              <button className={styles.primaryButton} onClick={handleConfirmationClose}>
                {strings.ticketPromptGotItLabel}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="notification-email-prompt" className={styles.promptTitle}>
              {strings.ticketPromptSetEmailTitle}
            </h2>
            <p className={styles.promptDescription}>{strings.ticketPromptCurrentEmailMsg}</p>
            <p className={styles.currentEmail}>{currentEmail}</p>
            <p className={styles.promptDescription}>{strings.ticketPromptDifferentEmailMsg}</p>
            <input
              type="email"
              className={styles.emailInput}
              placeholder={strings.ticketPromptEmailPlaceholder}
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label={strings.ticketPromptNewEmailAriaLabel}
            />
            {validationError && <span className={styles.errorMessage}>{validationError}</span>}
            <div className={styles.buttonRow}>
              <button
                className={styles.primaryButton}
                onClick={handleKeepCurrent}
                disabled={isSaving}
              >
                {strings.ticketPromptKeepCurrentLabel}
              </button>
              <button
                className={styles.secondaryButton}
                onClick={handleSaveNew}
                disabled={isSaving || !newEmail.trim()}
              >
                {isSaving ? strings.ticketSavingLabel : strings.ticketPromptUseNewLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
